import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  TreePine, 
  Flame, 
  Sprout,
  Target,
  DollarSign,
  CheckCircle2,
  Clock,
  ExternalLink,
  Circle,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Info,
  Globe,
  Ship,
  Wrench,
  Droplets,
  Zap,
  ThermometerSun,
  Wind,
  Leaf,
  TreeDeciduous,
  ArrowRight,
  Heart,
  Loader2,
  CreditCard
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

const DonationCategoryPage = () => {
  const { category } = useParams();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [showArgentinaDetails, setShowArgentinaDetails] = useState(false);
  const [showSoilComparison, setShowSoilComparison] = useState(false);

  // Stripe payment state
  const [selectedTier, setSelectedTier] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('general');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'cancelled', 'polling'

  const TIERS = [
    { id: 'tier_25', amount: 25 },
    { id: 'tier_50', amount: 50 },
    { id: 'tier_100', amount: 100 },
    { id: 'tier_250', amount: 250 },
    { id: 'tier_500', amount: 500 },
    { id: 'tier_1000', amount: 1000 },
    { id: 'tier_2500', amount: 2500 },
    { id: 'tier_5000', amount: 5000 },
  ];

  const FACILITIES = [
    { id: 'general', label: language === 'es' ? 'Fondo General Biosfera' : 'General Biosphere Fund' },
    { id: 'land', label: language === 'es' ? 'Adquisición del Terreno' : 'Land Acquisition' },
    { id: 'infrastructure', label: language === 'es' ? 'Infraestructura' : 'Infrastructure' },
    { id: 'education', label: language === 'es' ? 'Centro Educativo' : 'Education Center' },
    { id: 'restaurant', label: language === 'es' ? 'Restaurante Costero' : 'Lakeside Restaurant' },
    { id: 'dome', label: language === 'es' ? 'Domo de Eventos 720m²' : '720m² Event Dome' },
    { id: 'cabins', label: language === 'es' ? 'Eco-Cabañas' : 'Eco-Cabins' },
    { id: 'spa', label: language === 'es' ? 'Spa Japonés' : 'Japanese Spa' },
    { id: 'pools', label: language === 'es' ? 'Piscinas' : 'Swimming Pools' },
    { id: 'landscaping', label: language === 'es' ? 'Paisajismo' : 'Landscaping' },
  ];

  // Poll payment status on return from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus('success');
      return;
    }
    try {
      const res = await fetch(`${API}/payments/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      }
      if (data.status === 'expired') {
        setPaymentStatus('cancelled');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setPaymentStatus('success');
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') {
      setPaymentStatus('polling');
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
      toast.error(language === 'es' ? 'Pago cancelado' : 'Payment cancelled');
    }
  }, [searchParams, pollPaymentStatus, language]);

  const handleCheckout = async () => {
    const tier = selectedTier || (customAmount ? 'custom' : null);
    if (!tier) {
      toast.error(language === 'es' ? 'Seleccioná un monto' : 'Select a donation amount');
      return;
    }
    setProcessingPayment(true);
    try {
      const res = await fetch(`${API}/payments/biosphere/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier,
          custom_amount: tier === 'custom' ? parseFloat(customAmount) : undefined,
          facility: selectedFacility,
          donor_name: donorName || 'Anonymous',
          donor_email: donorEmail,
          origin_url: window.location.origin,
        })
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Checkout failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Helper function to get translation with proper fallback
  const getText = (key, fallback) => {
    const translation = t(key);
    // If translation returns the key itself, use fallback
    return translation === key ? fallback : translation;
  };

  // Category data with phases and equipment
  const categoryData = {
    'depining': {
      icon: '🟫',
      color: 'amber',
      bgGradient: 'from-amber-500/20 to-amber-600/10',
      borderColor: 'border-amber-500',
      accentColor: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-500',
      goal: 19600000,
      raised: 125000,
      translationKey: 'category1',
      planSummary: {
        title: 'Five-Year Aggressive De-Pining Plan',
        objective: 'To permanently reduce wildfire risk across the Epuyén valley by removing invasive pine species through continuous, large-scale, year-round operations.',
        targetArea: '4,500 hectares',
        annualTarget: '900 hectares/year',
        duration: '5 years',
        operations: '12 months per year'
      },
      phases: [
        {
          id: 1,
          title: 'Year 1: Fleet Acquisition & Initial Operations',
          titleEs: 'Año 1: Adquisición de Flota y Operaciones Iniciales',
          description: 'Acquire core machinery fleet, establish operational base, begin first 900 hectares of treatment',
          descriptionEs: 'Adquirir flota de maquinaria principal, establecer base operativa, comenzar tratamiento de primeras 900 hectáreas',
          goal: 2500000,
          raised: 125000,
          status: 'active',
          milestones: ['4 high-volume mulchers acquired', 'Transport fleet operational', 'First 450ha treated']
        },
        {
          id: 2,
          title: 'Year 2: Full Operational Capacity',
          titleEs: 'Año 2: Capacidad Operativa Completa',
          description: 'Complete fleet deployment, achieve full 900 hectares/year capacity, treat priority fire-risk zones',
          descriptionEs: 'Completar despliegue de flota, alcanzar capacidad completa de 900 ha/año, tratar zonas prioritarias de riesgo',
          goal: 1200000,
          raised: 0,
          status: 'pending',
          milestones: ['Access mulchers deployed', 'Support equipment complete', '900ha treated']
        },
        {
          id: 3,
          title: 'Year 3: Valley Core Treatment',
          titleEs: 'Año 3: Tratamiento del Núcleo del Valle',
          description: 'Focus on central valley zones, establish firebreak corridors, begin maintenance of Year 1 zones',
          descriptionEs: 'Enfocarse en zonas centrales del valle, establecer corredores cortafuegos, iniciar mantenimiento de zonas del Año 1',
          goal: 900000,
          raised: 0,
          status: 'pending',
          milestones: ['Central zones cleared', 'Firebreak network established', 'Year 1 zones maintained']
        },
        {
          id: 4,
          title: 'Year 4: Peripheral Expansion',
          titleEs: 'Año 4: Expansión Periférica',
          description: 'Extend operations to peripheral areas, treat steep terrain and remote zones',
          descriptionEs: 'Extender operaciones a áreas periféricas, tratar terrenos empinados y zonas remotas',
          goal: 700000,
          raised: 0,
          status: 'future',
          milestones: ['Peripheral zones mapped', 'Remote areas accessed', '3,600ha cumulative treated']
        },
        {
          id: 5,
          title: 'Year 5: Completion & Permanent Capacity',
          titleEs: 'Año 5: Finalización y Capacidad Permanente',
          description: 'Complete 4,500 hectare target, establish permanent prevention infrastructure, transition to maintenance mode',
          descriptionEs: 'Completar objetivo de 4,500 hectáreas, establecer infraestructura permanente de prevención, transición a modo mantenimiento',
          goal: 500000,
          raised: 0,
          status: 'future',
          milestones: ['4,500ha target achieved', 'Maintenance protocols established', 'Long-term capacity secured']
        }
      ],
      equipment: [
        {
          name: 'Prinoth RAPTOR 800 High-Volume Mulcher',
          brand: 'Prinoth (or equivalent 450-500 HP)',
          description: 'Primary de-pining engines for removal of dense pine stands, processing large diameter trees, continuous high-output mulching, and treatment of wide forest blocks.',
          cost: 325000,
          quantity: 4,
          priority: 'high',
          phase: 1,
          specs: ['450-500 HP', 'Tracked forestry mulcher', 'High-volume output', 'Dense stand capable'],
          whyRequired: 'These machines provide the core hectare throughput. Without them, annual targets cannot be reached.'
        },
        {
          name: 'Prinoth RAPTOR 300 Precision Mulcher',
          brand: 'Prinoth (or equivalent 275-300 HP)',
          description: 'Secondary support units for steep terrain, narrow corridors, near housing/infrastructure, finishing passes, and sensitive areas near water and trails.',
          cost: 280000,
          quantity: 2,
          priority: 'high',
          phase: 2,
          specs: ['275-300 HP', 'Precision control', 'Steep terrain capable', 'Low impact'],
          whyRequired: 'Essential for accessing difficult terrain and finishing work that high-volume mulchers cannot reach.'
        },
        {
          name: 'Excavator 20-30 Ton Class',
          brand: 'CAT 320/323, Komatsu PC210-PC300, or Volvo EC220-EC300',
          description: 'Terrain access and recovery: access creation, obstacle removal, machine recovery, slope stabilization, assisting mulchers in rocky terrain.',
          cost: 110000,
          quantity: 2,
          priority: 'high',
          phase: 1,
          specs: ['20-30 ton class', 'Hydraulic grapple', 'Rake attachment', 'Bucket attachment'],
          whyRequired: 'Critical for creating access routes and supporting mulcher operations in challenging terrain.'
        },
        {
          name: 'Dozer D6 Class',
          brand: 'CAT D6 or Komatsu D65',
          description: 'Access roads, firebreak corridors, contour shaping, track maintenance, and emergency fire line creation.',
          cost: 300000,
          quantity: 1,
          priority: 'high',
          phase: 1,
          specs: ['D6 class', 'Blade width 3.5m+', 'Ripper attachment', 'GPS grade control'],
          whyRequired: 'Essential for creating and maintaining access infrastructure and emergency firebreaks.'
        },
        {
          name: 'Wheel/Track Loader',
          brand: 'CAT 950-966, Volvo L90-L120, or tracked equivalent',
          description: 'Site logistics: moving guards, tools, materials, support operations, loading chippers if needed.',
          cost: 160000,
          quantity: 1,
          priority: 'medium',
          phase: 2,
          specs: ['3-4 cubic yard bucket', 'Quick-attach forks', 'High lift capacity'],
          whyRequired: 'Provides essential logistics support for continuous operations.'
        },
        {
          name: 'Lowboy Trailers + Prime Movers',
          brand: 'Various heavy-duty manufacturers',
          description: 'Constant relocation of heavy machinery between treatment zones across the valley.',
          cost: 200000,
          quantity: 2,
          priority: 'high',
          phase: 1,
          specs: ['50+ ton capacity', 'Hydraulic ramps', 'Air brakes', 'Prime mover included'],
          whyRequired: 'Required for continuous zone-to-zone operations across the 4,500 hectare target area.'
        },
        {
          name: 'Mobile Fuel Tanker',
          brand: 'Custom/Industrial',
          description: 'On-site refueling capability to maintain continuous operations without returning to base.',
          cost: 80000,
          quantity: 1,
          priority: 'high',
          phase: 1,
          specs: ['10,000-15,000 L capacity', 'Pump system', 'Safety compliant', 'Off-road capable'],
          whyRequired: 'Eliminates downtime from refueling trips, critical for 12-month continuous operations.'
        },
        {
          name: 'Service Truck (Field Workshop)',
          brand: 'Custom/Industrial',
          description: 'Mobile maintenance and repair capability for field operations.',
          cost: 120000,
          quantity: 1,
          priority: 'high',
          phase: 1,
          specs: ['Welding equipment', 'Hydraulic tools', 'Parts storage', 'Crane/lift'],
          whyRequired: 'Enables rapid response to mechanical issues, minimizing operational downtime.'
        },
        {
          name: '4x4 Pickup Vehicles',
          brand: 'Toyota Hilux, Ford Ranger, or equivalent',
          description: 'Logistics, supervision, and personnel transport across treatment zones.',
          cost: 45000,
          quantity: 4,
          priority: 'medium',
          phase: 1,
          specs: ['4x4 diesel', 'Double cab', 'Off-road capable', 'Tow package'],
          whyRequired: 'Essential for daily logistics, supervision, and rapid response across the operational area.'
        }
      ],
      capexBreakdown: [
        { category: 'High-volume mulchers (4 units)', categoryEs: 'Mulchers de alto volumen (4 unidades)', cost: 1300000 },
        { category: 'Access/Precision mulchers (2 units)', categoryEs: 'Mulchers de acceso/precisión (2 unidades)', cost: 560000 },
        { category: 'Excavators (2 units)', categoryEs: 'Excavadoras (2 unidades)', cost: 220000 },
        { category: 'Dozer (1 unit)', categoryEs: 'Bulldozer (1 unidad)', cost: 300000 },
        { category: 'Loader (1 unit)', categoryEs: 'Cargador (1 unidad)', cost: 160000 },
        { category: 'Transport & logistics fleet', categoryEs: 'Flota de transporte y logística', cost: 760000 },
        { category: 'Manual crew salaries (50 workers × 5 years)', categoryEs: 'Salarios de cuadrillas manuales (50 trabajadores × 5 años)', cost: 3000000 },
        { category: 'Chainsaws (50 units)', categoryEs: 'Motosierras (50 unidades)', cost: 50000 },
        { category: 'Safety gear & tools', categoryEs: 'Equipo de seguridad y herramientas', cost: 75000 }
      ],
      twoMethods: {
        intro: 'The de-pining of Epuyén cannot rely on machinery alone. Large areas of the valley contain mixed forest, where invasive pine grows intertwined with native species. In these zones, heavy machinery cannot be used without destroying the native forest.',
        approach: 'For this reason, the prevention plan combines heavy machinery for dense pine areas, manual surgical removal in mixed forests, and long-term local employment. This integrated approach allows true prevention without ecological damage.',
        mechanical: {
          title: 'Mechanical De-Pining',
          description: 'Used in dense pine stands where no native forest remains.',
          items: [
            'Forestry mulchers',
            'Excavators and support machinery',
            'Large-scale fuel reduction'
          ],
          note: 'This is the fastest method and covers large surfaces.'
        },
        manual: {
          title: 'Manual / Surgical De-Pining',
          description: 'Used in sensitive zones:',
          zones: [
            'Pine mixed with native forest',
            'Regeneration areas',
            'Riparian zones',
            'Buffer areas near water, homes, and trails'
          ],
          method: 'This work is done by trained crews using chainsaws, selectively removing invasive pine while preserving native species.',
          note: 'This method is slower — but ecologically essential.'
        }
      },
      employmentProgram: {
        title: 'Local Employment Program',
        intro: 'To carry out manual de-pining at scale, the project includes a 5-year employment program.',
        workers: 50,
        salary: 1000,
        duration: 60,
        totalSalaries: 3000000,
        priority: 'Priority hiring from Epuyén and surrounding communities',
        equipment: {
          chainsaws: { quantity: 50, costPerUnit: 1000, total: 50000 },
          safetyGear: { costPerWorker: 1500, total: 75000 },
          items: ['Safety gear (helmets, chaps, boots, gloves)', 'Spare chains, bars, fuel mix', 'Maintenance tools']
        }
      },
      manualCostBreakdown: [
        { category: 'Salaries (50 workers × 5 years)', categoryEs: 'Salarios (50 trabajadores × 5 años)', cost: 3000000 },
        { category: 'Chainsaws (50 units)', categoryEs: 'Motosierras (50 unidades)', cost: 50000 },
        { category: 'Safety & tools', categoryEs: 'Seguridad y herramientas', cost: 75000 }
      ],
      combinedStrategy: [
        'Mechanical de-pining (heavy machinery)',
        'Manual surgical removal',
        'Local job creation',
        'Ecological protection of native forest',
        'Permanent fire-risk reduction'
      ],
      socialImpact: [
        'Reduce wildfire risk at scale',
        'Protect native forest remnants',
        'Restore soil and humidity',
        'Generate 50 stable local jobs for five years',
        'Transfer forestry skills to the community',
        'Build long-term prevention capacity'
      ],
      transparency: [
        'Mapped by zone',
        'Documented monthly',
        'Reported in hectares treated',
        'Linked to crews and equipment',
        'Published on the Epuyén Renace platform'
      ],
      strategicImportance: [
        'Permanent fire-prevention capacity',
        'Rapid intervention capability',
        'Independence from emergency-only responses',
        'Ability to maintain treated zones long-term'
      ],
      conclusion: 'This approach is not symbolic — it is structural. The aggressive 5-year de-pining plan is only achievable with a dedicated, professional forestry fleet combined with trained manual crews. This transforms Epuyén from a reactive disaster zone into a region with permanent wildfire-prevention infrastructure. It is an investment not only in forests, but in long-term safety, water security, community resilience, and 50 local jobs.'
    },
    'firefighting': {
      icon: '🚒',
      color: 'red',
      bgGradient: 'from-red-500/20 to-red-600/10',
      borderColor: 'border-red-500',
      accentColor: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-500',
      goal: 1450000,
      raised: 85000,
      translationKey: 'category2',
      planSummary: {
        title: 'Firefighting Boat System for Lago Epuyén',
        objective: 'To establish a permanent firefighting boat unit capable of rapid deployment, high-capacity water pumping, and supporting ground brigades — transforming Lago Epuyén from a passive element into an active fire-defense asset.',
        vesselLength: '9-12 meters',
        pumpCapacity: '4,000-8,000 L/min',
        crew: '6-10 firefighters',
        propulsion: 'Electric/Hybrid'
      },
      phases: [
        {
          id: 1,
          title: 'Design & Engineering',
          titleEs: 'Diseño e Ingeniería',
          description: 'Complete vessel design, fire pump system integration, electrical/hybrid propulsion specifications',
          descriptionEs: 'Completar diseño del barco, integración del sistema de bombas, especificaciones de propulsión eléctrica/híbrida',
          goal: 150000,
          raised: 45000,
          status: 'active',
          milestones: ['Hull design finalized', 'Pump system specified', 'Hybrid propulsion designed']
        },
        {
          id: 2,
          title: 'Vessel Construction',
          titleEs: 'Construcción del Barco',
          description: 'Build aluminum/composite hull, install propulsion system, integrate firefighting equipment',
          descriptionEs: 'Construir casco de aluminio/compuesto, instalar sistema de propulsión, integrar equipo de combate',
          goal: 600000,
          raised: 35000,
          status: 'pending',
          milestones: ['Hull complete', 'Propulsion installed', 'Fire systems integrated']
        },
        {
          id: 3,
          title: 'Equipment Installation',
          titleEs: 'Instalación de Equipos',
          description: 'Install industrial fire pump, hose reels, safety systems, and crew equipment',
          descriptionEs: 'Instalar bomba industrial, carretes de manguera, sistemas de seguridad y equipo de tripulación',
          goal: 400000,
          raised: 5000,
          status: 'pending',
          milestones: ['Pump operational', 'Hose systems ready', 'Safety equipment installed']
        },
        {
          id: 4,
          title: 'Testing & Deployment',
          titleEs: 'Pruebas y Despliegue',
          description: 'Lake trials, crew training, integration with local brigades, operational certification',
          descriptionEs: 'Pruebas en lago, entrenamiento de tripulación, integración con brigadas locales, certificación operacional',
          goal: 200000,
          raised: 0,
          status: 'future',
          milestones: ['Sea trials complete', 'Crew certified', 'Integrated with emergency services']
        },
        {
          id: 5,
          title: 'Permanent Operations',
          titleEs: 'Operaciones Permanentes',
          description: 'Full operational capacity, 24/7 readiness, ongoing training and maintenance',
          descriptionEs: 'Capacidad operativa completa, preparación 24/7, entrenamiento y mantenimiento continuo',
          goal: 100000,
          raised: 0,
          status: 'future',
          milestones: ['24/7 readiness achieved', 'Patrol schedule active', 'Maintenance protocols established']
        }
      ],
      equipment: [
        {
          name: 'Firefighting Vessel (Custom Build)',
          brand: 'Custom / Adapted Design',
          description: 'Aluminum or composite hull vessel, shallow draft, high stability, designed for cold mountain lakes. 9-12 meters length with crew capacity of 6-10 firefighters.',
          cost: 475000,
          quantity: 1,
          priority: 'high',
          phase: 2,
          specs: ['9-12m length', 'Aluminum/composite hull', 'Shallow draft', 'High stability', 'Cold lake design'],
          whyRequired: 'The core vessel that enables water-based firefighting operations. Without it, the lake remains an unused resource during emergencies.'
        },
        {
          name: 'Industrial Fire Pump System',
          brand: 'Rosenbauer-class or equivalent',
          description: 'High-capacity industrial-grade fire pump with continuous operation capability. Direct lake intake for unlimited water supply.',
          cost: 225000,
          quantity: 1,
          priority: 'high',
          phase: 3,
          specs: ['4,000-8,000 L/min capacity', 'Continuous operation', 'Direct lake intake', 'Professional grade'],
          whyRequired: 'Provides the massive water throughput needed to attack wildfires effectively. Standard boat pumps are insufficient for firefighting.'
        },
        {
          name: 'Hose Systems & Reels',
          brand: 'Professional firefighting grade',
          description: 'Long-distance hose deployment system capable of pumping water hundreds of meters uphill to reach fires on mountain slopes.',
          cost: 115000,
          quantity: 1,
          priority: 'high',
          phase: 3,
          specs: ['Long-distance capability', 'Uphill pumping', 'Quick deployment reels', 'Multiple diameter hoses'],
          whyRequired: 'Enables the boat to attack fires on slopes far from the shoreline, multiplying the effective range of the water source.'
        },
        {
          name: 'Electric/Hybrid Propulsion System',
          brand: 'Marine electric systems',
          description: 'High torque electric or hybrid-electric propulsion for fast acceleration, low noise, and zero lake contamination.',
          cost: 225000,
          quantity: 1,
          priority: 'high',
          phase: 2,
          specs: ['High torque', 'Fast acceleration', 'Silent operation', 'Zero contamination', 'Night response capable'],
          whyRequired: 'Protects the pristine lake environment while enabling silent night operations and rapid response without fuel dependency.'
        },
        {
          name: 'Safety & Firefighting Equipment',
          brand: 'Various professional brands',
          description: 'Complete crew safety gear, personal protective equipment, communication systems, and firefighting accessories.',
          cost: 75000,
          quantity: 1,
          priority: 'high',
          phase: 3,
          specs: ['PPE for 10 crew', 'Radio communications', 'Life safety systems', 'Medical kit', 'Navigation equipment'],
          whyRequired: 'Ensures crew safety during dangerous firefighting operations and enables coordination with ground brigades.'
        }
      ],
      capexBreakdown: [
        { category: 'Vessel (custom or adapted)', cost: 475000 },
        { category: 'Fire pump system', cost: 225000 },
        { category: 'Hose systems & reels', cost: 115000 },
        { category: 'Electrical/hybrid system', cost: 225000 },
        { category: 'Safety & firefighting equipment', cost: 75000 }
      ],
      geographyChallenges: [
        'Steep forested slopes',
        'Limited road access',
        'Strong valley winds'
      ],
      boatCapabilities: [
        'Reach fire fronts faster than trucks',
        'Pump unlimited water from the lake',
        'Operate even when roads are blocked',
        'Attack fires from below the slope',
        'Reduce response time by 60-80%'
      ],
      operationalFunctions: [
        'Active wildfire suppression',
        'Early attack during ignition',
        'Support of land brigades',
        'Structure protection near lake zone',
        'Patrol and rapid inspection',
        'Training and preparedness'
      ],
      integration: [
        'Municipal fire brigades',
        'Provincial emergency services',
        'Forest fire response units'
      ],
      environmentalBenefits: [
        'No fuel spills into the lake',
        'Reduced helicopter dependency',
        'Minimal environmental footprint',
        'Silent operation during night response'
      ],
      strategicImportance: [
        'Permanent water-based firefighting capacity',
        'Rapid response capability within minutes',
        'Lower suppression costs',
        'Reduced fire spread probability',
        'Protection of homes, forests, and water sources'
      ],
      conclusion: 'A dedicated firefighting boat on Lago Epuyén represents one of the most efficient investments in wildfire prevention and response. It transforms the lake into a frontline defense system — protecting the valley before fires become disasters.'
    },
    'reforestation': {
      icon: '🌱',
      color: 'green',
      bgGradient: 'from-green-500/20 to-green-600/10',
      borderColor: 'border-green-500',
      accentColor: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-500',
      goal: 250000,
      raised: 47250,
      translationKey: 'category3',
      phases: [
        {
          id: 1,
          title: 'Nursery Establishment',
          titleEs: 'Establecimiento de Vivero',
          description: 'Build greenhouse, source native seeds, begin seedling production',
          descriptionEs: 'Construir invernadero, obtener semillas nativas, iniciar producción de plantines',
          goal: 45000,
          raised: 35000,
          status: 'active',
          milestones: ['Greenhouse built', 'Seeds sourced', '5,000 seedlings growing']
        },
        {
          id: 2,
          title: 'First 10,000 Trees',
          titleEs: 'Primeros 10,000 Árboles',
          description: 'Plant first wave of native trees with GPS tracking and monitoring',
          descriptionEs: 'Plantar primera oleada de árboles nativos con seguimiento GPS y monitoreo',
          goal: 75000,
          raised: 10000,
          status: 'active',
          milestones: ['2,500 planted', '5,000 planted', '10,000 planted']
        },
        {
          id: 3,
          title: 'Expansion to 30,000 Trees',
          titleEs: 'Expansión a 30,000 Árboles',
          description: 'Scale up planting operations with community involvement',
          descriptionEs: 'Escalar operaciones de plantación con participación comunitaria',
          goal: 80000,
          raised: 2250,
          status: 'pending',
          milestones: ['20,000 trees', '25,000 trees', '30,000 trees']
        },
        {
          id: 4,
          title: 'Long-term Monitoring & Maintenance',
          titleEs: 'Monitoreo y Mantenimiento a Largo Plazo',
          description: 'Establish permanent monitoring system and maintenance crews',
          descriptionEs: 'Establecer sistema permanente de monitoreo y equipos de mantenimiento',
          goal: 50000,
          raised: 0,
          status: 'future',
          milestones: ['IoT sensors deployed', 'Maintenance team hired', '5-year plan active']
        }
      ],
      equipment: [
        {
          name: 'Native Tree Seedlings (Lenga)',
          brand: 'Epuyén Nursery',
          description: 'Nothofagus pumilio seedlings grown from local seed sources.',
          cost: 15,
          quantity: 5000,
          priority: 'high',
          phase: 2,
          specs: ['30-50cm height', 'Root-pruned', 'Mycorrhizal inoculated']
        },
        {
          name: 'Native Tree Seedlings (Coihue)',
          brand: 'Epuyén Nursery',
          description: 'Nothofagus dombeyi seedlings. Evergreen species.',
          cost: 18,
          quantity: 3000,
          priority: 'high',
          phase: 2,
          specs: ['40-60cm height', 'Container-grown', 'Disease-free certified']
        },
        {
          name: 'Native Tree Seedlings (Ciprés)',
          brand: 'Epuyén Nursery',
          description: 'Austrocedrus chilensis seedlings. Native conifer.',
          cost: 20,
          quantity: 2000,
          priority: 'medium',
          phase: 2,
          specs: ['25-40cm height', 'Slow-growing', 'Drought tolerant']
        },
        {
          name: 'Greenhouse Structure',
          brand: 'Custom / Local Construction',
          description: 'Professional greenhouse for seedling production.',
          cost: 35000,
          quantity: 1,
          priority: 'high',
          phase: 1,
          specs: ['200 m² growing area', 'Automated irrigation', 'Shade cloth', '10,000 capacity']
        },
        {
          name: 'GPS Tree Tracking System',
          brand: 'Custom Development',
          description: 'Software and hardware for individual tree tracking.',
          cost: 25000,
          quantity: 1,
          priority: 'medium',
          phase: 2,
          specs: ['10,000 tree capacity', 'Mobile app', 'Photo documentation', 'Growth tracking']
        },
        {
          name: 'Irrigation System (Drip)',
          brand: 'Netafim / Local',
          description: 'Drip irrigation system for establishing young plantings.',
          cost: 15000,
          quantity: 1,
          priority: 'medium',
          phase: 3,
          specs: ['5 hectare coverage', 'Solar pump option', 'Fertigation capable']
        },
        {
          name: 'Proprietary Reforestation Drone System',
          brand: 'Custom Development — Epuyén Renace',
          description: 'Purpose-built drones for deploying seed balls (earth balls) into steep, burned, or inaccessible zones. Complements manual planting where humans cannot safely work.',
          cost: 45000,
          quantity: 2,
          priority: 'high',
          phase: 3,
          featured: true,
          specs: ['GPS-mapped deployment', 'Seed ball release system', 'Species-specific zones', 'Geo-logged missions', 'Steep terrain capable']
        }
      ]
    },
    'contamination': {
      icon: '♻️',
      color: 'cyan',
      bgGradient: 'from-cyan-500/20 to-cyan-600/10',
      borderColor: 'border-cyan-500',
      accentColor: 'text-cyan-600 dark:text-cyan-400',
      bgColor: 'bg-cyan-500',
      goal: 50000,
      raised: 8500,
      translationKey: 'category4',
      isResearchPhase: true,
      researchPhase: {
        title: 'Waste Management & Contamination',
        context: 'Through on-the-ground observation and community dialogue, we have identified that Epuyén faces a significant open waste and contamination challenge, including informal dumping areas that pose long-term risks to soil, water, and public health.',
        statement: 'This issue cannot be addressed through improvisation or isolated actions.',
        currentPhase: 'Research & Diagnostic Phase',
        focusAreas: [
          'Understanding existing waste flows',
          'Identifying contamination risks',
          'Mapping open dumping zones',
          'Studying environmental and health impacts',
          'Reviewing viable models used in similar rural and mountain communities'
        ],
        requirement: 'Waste management requires technical accuracy, regulatory coordination, and long-term planning.',
        ethos: 'As part of our ethos, no solution will be announced without proper research.',
        commitment: 'Once this assessment phase is completed, Epuyén Renace will publish a clear, public, and transparent waste-management proposal, open for community review and institutional dialogue.',
        objective: 'The objective is not to impose a solution, but to build one that is realistic, sustainable, and appropriate for Epuyén\'s scale and geography.',
        importance: 'Addressing contamination is essential to protecting water sources, restoring ecosystems, and ensuring a healthy future for the community.'
      },
      phases: [
        {
          id: 1,
          title: 'Community Observation & Dialogue',
          titleEs: 'Observación Comunitaria y Diálogo',
          description: 'On-the-ground assessment of waste challenges through community engagement',
          descriptionEs: 'Evaluación en terreno de los desafíos de residuos a través del compromiso comunitario',
          goal: 10000,
          raised: 6500,
          status: 'active',
          milestones: ['Initial observations complete', 'Community meetings held', 'Key stakeholders identified']
        },
        {
          id: 2,
          title: 'Waste Flow Mapping & Risk Assessment',
          titleEs: 'Mapeo de Flujos de Residuos y Evaluación de Riesgos',
          description: 'Technical mapping of dumping zones, contamination risks, and environmental impacts',
          descriptionEs: 'Mapeo técnico de zonas de vertido, riesgos de contaminación e impactos ambientales',
          goal: 15000,
          raised: 2000,
          status: 'pending',
          milestones: ['Dumping zones mapped', 'Water quality tested', 'Health impacts assessed']
        },
        {
          id: 3,
          title: 'Model Research & Analysis',
          titleEs: 'Investigación y Análisis de Modelos',
          description: 'Study viable waste management models from similar rural and mountain communities',
          descriptionEs: 'Estudiar modelos viables de gestión de residuos de comunidades rurales y de montaña similares',
          goal: 15000,
          raised: 0,
          status: 'pending',
          milestones: ['Comparable models identified', 'Best practices documented', 'Feasibility analyzed']
        },
        {
          id: 4,
          title: 'Public Proposal & Community Review',
          titleEs: 'Propuesta Pública y Revisión Comunitaria',
          description: 'Publish transparent waste-management proposal for community and institutional dialogue',
          descriptionEs: 'Publicar propuesta transparente de gestión de residuos para diálogo comunitario e institucional',
          goal: 10000,
          raised: 0,
          status: 'future',
          milestones: ['Proposal drafted', 'Community review period', 'Institutional coordination']
        }
      ],
      equipment: []
    },
    'biosphere': {
      icon: '🏛️',
      color: 'purple',
      bgGradient: 'from-purple-500/20 to-purple-600/10',
      borderColor: 'border-purple-500',
      accentColor: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-500',
      goal: 8000000,
      raised: 15000,
      translationKey: 'category5',
      phases: [
        {
          id: 1,
          title: 'Phase 1: Land + Core Setup',
          titleEs: 'Fase 1: Terreno + Configuración Base',
          description: 'Secure 33 hectares of lakefront land. Install infrastructure: roads, utilities, water systems.',
          descriptionEs: 'Asegurar 33 hectáreas de terreno frente al lago. Instalar infraestructura: caminos, servicios, sistemas de agua.',
          goal: 3000000,
          raised: 15000,
          status: 'active',
          milestones: ['Land acquisition complete', 'Roads & utilities installed', 'Water systems operational']
        },
        {
          id: 2,
          title: 'Phase 2: Core Identity Build',
          titleEs: 'Fase 2: Construcción de Identidad Central',
          description: 'Build the Biocentric Education Center, native species greenhouse nursery, lakeside restaurant with deck, and 720m² event dome.',
          descriptionEs: 'Construir Centro de Educación Biocéntrica, vivero de invernaderos de especies nativas, restaurante costero con deck y domo de eventos de 720m².',
          goal: 1800000,
          raised: 0,
          status: 'pending',
          milestones: ['Education center open', 'Nursery producing seedlings', 'Restaurant operational', 'Event dome complete']
        },
        {
          id: 3,
          title: 'Phase 3: Revenue Engine',
          titleEs: 'Fase 3: Motor de Ingresos',
          description: '10–15 eco-cabins, Japanese onsen spa complex, Olympic 4-season pool + organic bio-pool with cafeteria. Full operations launch.',
          descriptionEs: '10–15 eco-cabañas, complejo spa tipo onsen japonés, piscina olímpica 4 estaciones + bio-piscina orgánica con cafetería. Lanzamiento de operaciones.',
          goal: 3200000,
          raised: 0,
          status: 'future',
          milestones: ['Cabins ready', 'Spa operational', 'Pools open', 'Full staff hired']
        }
      ],
      equipment: [
        {
          name: 'Land Acquisition (33 ha)',
          brand: 'Lakefront, Epuyén',
          description: '33 hectares of lakefront land at Lago Epuyén — the foundation of the entire biosphere park.',
          cost: 2000000,
          quantity: 1,
          priority: 'critical',
          phase: 1,
          facility: 'land',
          specs: ['33 hectares', 'Lakefront access', 'Forest & valley zone', 'Legal framework']
        },
        {
          name: 'Infrastructure & Utilities',
          brand: 'Core Setup',
          description: 'Roads, water systems, electrical grid, sewage treatment, and core infrastructure for the entire park.',
          cost: 750000,
          quantity: 1,
          priority: 'critical',
          phase: 1,
          facility: 'infrastructure',
          specs: ['Access roads', 'Water/sewage', 'Electrical grid', 'Biomass heating system']
        },
        {
          name: 'Biocentric Education Center + Nursery',
          brand: 'Ecological Design',
          description: 'Education center for biocentrism and fire ecology, paired with professional native species greenhouses.',
          cost: 550000,
          quantity: 1,
          priority: 'high',
          phase: 2,
          facility: 'education',
          specs: ['Classroom capacity 100+', 'Workshop spaces', '10,000 seedling nursery', 'Seed bank']
        },
        {
          name: 'Lakeside Restaurant + Deck',
          brand: 'Custom Timber',
          description: 'Restaurant with 100% local food supply chain. Large wooden deck over Lago Epuyén. Fire boat dock.',
          cost: 700000,
          quantity: 1,
          priority: 'high',
          phase: 2,
          facility: 'restaurant',
          specs: ['100-seat restaurant', 'Deck over lake', 'Fire boat dock', 'Public cafeteria']
        },
        {
          name: '720m² Event Dome',
          brand: 'Timber Geodesic',
          description: '720-square-meter geodesic dome built with timber frame. Conferences, cultural events, weddings.',
          cost: 400000,
          quantity: 1,
          priority: 'high',
          phase: 2,
          facility: 'dome',
          specs: ['720m² floor area', 'Timber frame structure', 'All-season use', 'Event capacity 300+']
        },
        {
          name: '10–15 Eco-Cabins',
          brand: 'Low-Density Forest',
          description: 'Private forest cabins nestled in native woodland. Low density, high value. Minimal ecological footprint.',
          cost: 1500000,
          quantity: 1,
          priority: 'high',
          phase: 3,
          facility: 'cabins',
          specs: ['10–15 units', 'Forest-integrated', 'Private terraces', 'Sustainable materials']
        },
        {
          name: 'Japanese Spa Complex',
          brand: 'Onsen Patagonia',
          description: 'Japanese-inspired onsen spa adapted to Patagonia. Sauna, cold plunge, hot pools, meditation spaces.',
          cost: 750000,
          quantity: 1,
          priority: 'high',
          phase: 3,
          facility: 'spa',
          specs: ['Hot pools (onsen)', 'Sauna + cold plunge', 'Meditation garden', 'Treatment rooms']
        },
        {
          name: 'Olympic + Bio Pool Complex',
          brand: 'Aquatic Engineering',
          description: 'Olympic-size 4-season heated pool + large organic bio-pool with natural plant filtration. Shared water engineering.',
          cost: 1000000,
          quantity: 1,
          priority: 'medium',
          phase: 3,
          facility: 'pools',
          specs: ['Olympic 50m heated pool', 'Organic bio-pool', 'Shared water system', 'Year-round cafeteria']
        },
        {
          name: 'Landscaping & Trail Network',
          brand: 'Conservation Design',
          description: 'Boardwalk trail network, firebreak corridors, water reservoirs, and native landscape restoration.',
          cost: 350000,
          quantity: 1,
          priority: 'medium',
          phase: 1,
          facility: 'landscaping',
          specs: ['Trail network', 'Firebreak corridors', 'Water reservoirs', 'Native planting']
        }
      ],
      economicImpact: {
        location: 'Epuyén, Chubut – Patagonia Andina',
        area: '33 hectares (Lakefront and forest valley zone)',
        directJobs: {
          min: 150,
          max: 200,
          type: 'permanent, year-round',
          positions: [
            'Park and land management',
            'Environmental restoration',
            'Nursery and reforestation',
            'Education and training',
            'Wellness and hospitality services',
            'Swimming and sports instruction',
            'Food production and service',
            'Maintenance, operations, and administration'
          ],
          priority: [
            'Residents of Epuyén',
            'Surrounding communities',
            'Workers affected by the fires'
          ]
        },
        indirectJobs: {
          estimate: '+30% to 60% additional jobs over time',
          sectors: [
            'Local food suppliers',
            'Agricultural producers',
            'Transport services',
            'Construction and maintenance contractors',
            'Artisans and regional producers',
            'Tourism-related services'
          ]
        },
        economicCirculation: [
          'Stable monthly payroll',
          'Long-term service contracts',
          'Local procurement policies',
          'Year-round operation (non-seasonal)'
        ],
        benefits: [
          'Income stability',
          'Reduction of seasonal employment dependency',
          'Retention of families in the region',
          'Increased local consumption'
        ],
        tourismProfile: [
          'Educational destination',
          'Wellness and ecological tourism hub',
          'Public park and community space',
          'Regional training and cultural center'
        ],
        regionalIntegration: ['Lago Puelo', 'El Hoyo', 'El Maitén', 'Comarca Andina'],
        environmentalServices: [
          'Reduction of wildfire risk',
          'Restoration of native forest',
          'Protection of water sources',
          'Improvement of soil stability',
          'Recovery of ecosystem functions',
          'Waste and contamination mitigation'
        ],
        publicSavings: [
          'Emergency response',
          'Firefighting',
          'Infrastructure loss',
          'Post-disaster reconstruction'
        ],
        municipalValue: [
          'Permanent employment generation',
          'Ecological restoration at scale',
          'Increased resilience to future fires',
          'Strengthened regional identity',
          'Attraction of external funding and donations',
          'Positioning Epuyén as a national reference in post-fire recovery'
        ]
      }
    }
  };

  const data = categoryData[category];
  
  // Redirect biosphere category to unified /biosphere page
  if (category === 'biosphere') {
    window.location.href = '/biosphere';
    return null;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-muted-foreground">Category not found</p>
        </div>
      </div>
    );
  }

  const progressPercent = Math.min((data.raised / data.goal) * 100, 100);
  const totalEquipmentCost = data.equipment.reduce((sum, item) => sum + (item.cost * item.quantity), 0);

  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />;
      case 'pending': return <Clock className="w-4 h-4 text-amber-500" />;
      case 'future': return <Circle className="w-4 h-4 text-muted-foreground" />;
      case 'complete': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      default: return <Circle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'active': return t('donate.active');
      case 'pending': return t('donate.pending');
      case 'future': return t('donate.future');
      case 'complete': return t('donate.complete');
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className={`py-16 px-4 bg-gradient-to-b ${data.bgGradient}`}>
        <div className="max-w-6xl mx-auto">
          <Link to="/#donate" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-6"
          >
            <div className="text-6xl">{data.icon}</div>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
                {t(`landing.donationCategories.${data.translationKey}.title`)}
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                {t(`landing.donationCategories.${data.translationKey}.description`)}
              </p>
              
              {/* Overall Progress Bar */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-muted-foreground">Overall Category Progress</span>
                  <span className={`font-bold ${data.accentColor}`}>{progressPercent.toFixed(1)}%</span>
                </div>
                <div className="h-4 bg-muted rounded-full overflow-hidden mb-3">
                  <motion.div 
                    className={`h-full rounded-full`}
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    style={{ backgroundColor: data.color === 'amber' ? '#f59e0b' : data.color === 'red' ? '#ef4444' : '#22c55e' }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground font-medium">${data.raised.toLocaleString()} raised</span>
                  <span className="text-muted-foreground">Goal: ${data.goal.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stripe Payment Section - Biosphere Only */}
      {data.hasStripePayment && (
        <section className="py-12 px-4 bg-gradient-to-b from-purple-500/5 to-background border-b border-border">
          <div className="max-w-4xl mx-auto">
            {/* Success State */}
            {paymentStatus === 'success' && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {language === 'es' ? '¡Gracias por tu donación!' : 'Thank You for Your Donation!'}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {language === 'es'
                    ? 'Tu contribución nos acerca a hacer realidad el parque biosfera.'
                    : 'Your contribution brings us closer to making the biosphere park a reality.'}
                </p>
                <Button onClick={() => setPaymentStatus(null)} variant="outline" data-testid="donate-again-btn">
                  {language === 'es' ? 'Hacer otra donación' : 'Make Another Donation'}
                </Button>
              </motion.div>
            )}

            {/* Polling State */}
            {paymentStatus === 'polling' && (
              <div className="text-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-purple-500 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'es' ? 'Verificando tu pago...' : 'Verifying your payment...'}
                </p>
              </div>
            )}

            {/* Donation Form */}
            {!paymentStatus && paymentStatus !== 'polling' && (
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <div className="text-center mb-8">
                  <Heart className="w-8 h-8 text-purple-500 mx-auto mb-3" />
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {language === 'es' ? 'Donar al Proyecto Biosfera' : 'Donate to the Biosphere Project'}
                  </h2>
                  <p className="text-muted-foreground max-w-lg mx-auto text-sm">
                    {language === 'es'
                      ? 'Cada contribución nos acerca a asegurar las 33 hectáreas y construir un futuro regenerativo.'
                      : 'Every contribution brings us closer to securing 33 hectares and building a regenerative future.'}
                  </p>
                </div>

                <Card className="bg-card border-border">
                  <CardContent className="p-6 space-y-6">
                    {/* Amount Selection */}
                    <div>
                      <label className="text-sm font-semibold text-foreground mb-3 block">
                        {language === 'es' ? 'Seleccionar monto' : 'Select Amount'}
                      </label>
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {TIERS.map((tier) => (
                          <button
                            key={tier.id}
                            onClick={() => { setSelectedTier(tier.id); setCustomAmount(''); }}
                            className={`py-2.5 px-3 rounded-lg text-sm font-semibold border transition-all ${
                              selectedTier === tier.id
                                ? 'bg-purple-500 text-white border-purple-500'
                                : 'bg-muted/50 text-foreground border-border hover:border-purple-500/50'
                            }`}
                            data-testid={`tier-${tier.amount}`}
                          >
                            ${tier.amount.toLocaleString()}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{language === 'es' ? 'o monto personalizado:' : 'or custom amount:'}</span>
                        <Input
                          type="number"
                          min="10"
                          placeholder="$"
                          value={customAmount}
                          onChange={(e) => { setCustomAmount(e.target.value); setSelectedTier(null); }}
                          className="w-32"
                          data-testid="custom-amount-input"
                        />
                      </div>
                    </div>

                    {/* Facility Selection */}
                    <div>
                      <label className="text-sm font-semibold text-foreground mb-3 block">
                        {language === 'es' ? 'Destinar a (opcional)' : 'Earmark for (optional)'}
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {FACILITIES.map((f) => (
                          <button
                            key={f.id}
                            onClick={() => setSelectedFacility(f.id)}
                            className={`py-2 px-3 rounded-lg text-xs font-medium border transition-all text-left ${
                              selectedFacility === f.id
                                ? 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/40'
                                : 'bg-muted/30 text-muted-foreground border-border hover:border-purple-500/30'
                            }`}
                            data-testid={`facility-${f.id}`}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Donor Info */}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">
                          {language === 'es' ? 'Nombre (opcional)' : 'Name (optional)'}
                        </label>
                        <Input
                          value={donorName}
                          onChange={(e) => setDonorName(e.target.value)}
                          placeholder={language === 'es' ? 'Tu nombre' : 'Your name'}
                          data-testid="donor-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">
                          {language === 'es' ? 'Email (opcional)' : 'Email (optional)'}
                        </label>
                        <Input
                          type="email"
                          value={donorEmail}
                          onChange={(e) => setDonorEmail(e.target.value)}
                          placeholder="email@example.com"
                          data-testid="donor-email-input"
                        />
                      </div>
                    </div>

                    {/* Checkout Button */}
                    <Button
                      onClick={handleCheckout}
                      disabled={processingPayment || (!selectedTier && !customAmount)}
                      size="lg"
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="checkout-button"
                    >
                      {processingPayment ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {language === 'es' ? 'Procesando...' : 'Processing...'}</>
                      ) : (
                        <><CreditCard className="w-5 h-5 mr-2" />
                          {language === 'es' ? 'Donar' : 'Donate'}{' '}
                          {selectedTier ? `$${TIERS.find(t => t.id === selectedTier)?.amount.toLocaleString()}` : customAmount ? `$${parseFloat(customAmount).toLocaleString()}` : ''}
                          {selectedFacility !== 'general' ? ` → ${FACILITIES.find(f => f.id === selectedFacility)?.label}` : ''}
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      {language === 'es'
                        ? 'Pago seguro procesado por Stripe. Serás redirigido a completar tu donación.'
                        : 'Secure payment processed by Stripe. You will be redirected to complete your donation.'}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Plan Summary Section - For De-Pining and Firefighting */}
      {data.planSummary && (
        <section className={`py-12 px-4 ${category === 'firefighting' ? 'bg-red-500/5 border-y border-red-500/20' : 'bg-amber-500/5 border-y border-amber-500/20'}`}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {category === 'firefighting' 
                    ? (t(`landing.donationCategories.${data.translationKey}.boatSystem.title`) || data.planSummary.title)
                    : (t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.title`) || data.planSummary.title)
                  }
                </h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  {category === 'firefighting'
                    ? (t(`landing.donationCategories.${data.translationKey}.boatSystem.objective`) || data.planSummary.objective)
                    : (t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.objective`) || data.planSummary.objective)
                  }
                </p>
              </div>
              
              {/* De-Pining specific metrics */}
              {category === 'depining' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-card rounded-xl p-4 border border-amber-500/30 text-center">
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.targetArea`)}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.targetArea')}</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-amber-500/30 text-center">
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.annualTarget`)}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.annualTarget')}</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-amber-500/30 text-center">
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.duration`)}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.duration')}</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-amber-500/30 text-center">
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.operations`)}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.operations')}</p>
                  </div>
                </div>
              )}

              {/* Firefighting Boat specific metrics */}
              {category === 'firefighting' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-card rounded-xl p-4 border border-red-500/30 text-center">
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                      {t(`landing.donationCategories.${data.translationKey}.boatSystem.vesselLength`) || data.planSummary.vesselLength}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.vesselLength') || 'Vessel Length'}</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-red-500/30 text-center">
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                      {t(`landing.donationCategories.${data.translationKey}.boatSystem.pumpCapacity`) || data.planSummary.pumpCapacity}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.pumpCapacity') || 'Pump Capacity'}</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-red-500/30 text-center">
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                      {t(`landing.donationCategories.${data.translationKey}.boatSystem.crew`) || data.planSummary.crew}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.crew') || 'Crew Capacity'}</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-red-500/30 text-center">
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                      {t(`landing.donationCategories.${data.translationKey}.boatSystem.propulsion`) || data.planSummary.propulsion}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('common.propulsion') || 'Propulsion'}</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Why Mulching Is Best - Only for De-Pining */}
      {category === 'depining' && (
        <section className="py-12 px-4 bg-gradient-to-b from-amber-500/5 to-background">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full text-amber-600 dark:text-amber-400 text-sm font-medium mb-4">
                <span className="text-lg">🌱</span>
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.title`) || 'Why Mulching Is the Best Option'}
              </div>
              <p className="text-muted-foreground max-w-3xl mx-auto text-lg">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.intro`) || 'Mulching means grinding the pine directly on site and returning the organic material to the soil.'}
              </p>
              <p className="text-foreground font-medium mt-2">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.fundamentalDifference`) || 'This is fundamentally different from removing wood or burning debris.'}
              </p>
            </motion.div>

            {/* Comparison Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* vs Removing */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-red-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <span className="text-xl">❌</span>
                  </div>
                  <h3 className="font-bold text-red-600 dark:text-red-400">
                    {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.vsRemoving.title`) || 'vs Removing Wood'}
                  </h3>
                </div>
                <ul className="space-y-2 mb-4">
                  {(() => {
                    const problems = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.vsRemoving.removingProblems`, { returnObjects: true });
                    const safeProblems = Array.isArray(problems) ? problems : [
                      'Dries and exposes soil',
                      'Removes organic matter',
                      'Destroys microbial life',
                      'Increases erosion',
                      'Requires heavy transport',
                      'Concentrates waste elsewhere'
                    ];
                    return safeProblems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ));
                  })()}
                </ul>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium italic border-t border-red-500/20 pt-3">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.vsRemoving.conclusion`) || 'Turns a forest into a biological desert.'}
                </p>
              </motion.div>

              {/* vs Burning */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-card rounded-xl border border-orange-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <span className="text-xl">🔥</span>
                  </div>
                  <h3 className="font-bold text-orange-600 dark:text-orange-400">
                    {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.vsBurning.title`) || 'vs Burning'}
                  </h3>
                </div>
                <ul className="space-y-2 mb-4">
                  {(() => {
                    const problems = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.vsBurning.burningProblems`, { returnObjects: true });
                    const safeProblems = Array.isArray(problems) ? problems : [
                      'Releases carbon immediately',
                      'Sterilizes soil',
                      'Kills fungi & microorganisms',
                      'Creates hydrophobic soils',
                      'Increases erosion risk'
                    ];
                    return safeProblems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Flame className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ));
                  })()}
                </ul>
                <p className="text-sm text-orange-600 dark:text-orange-400 font-medium italic border-t border-orange-500/20 pt-3">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.vsBurning.conclusion`) || 'Burned soils reject water, worsening floods.'}
                </p>
              </motion.div>

              {/* Mulching Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-xl">✅</span>
                  </div>
                  <h3 className="font-bold text-green-600 dark:text-green-400">
                    {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.benefits.title`) || 'Mulching Benefits'}
                  </h3>
                </div>
                <ul className="space-y-2 mb-4">
                  {(() => {
                    const benefits = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.benefits.items`, { returnObjects: true });
                    const safeBenefits = Array.isArray(benefits) ? benefits : [
                      'Returns carbon to soil',
                      'Protects from sun & wind',
                      'Retains moisture',
                      'Feeds microorganisms',
                      'Prevents erosion',
                      'Suppresses regrowth',
                      'Enables native planting'
                    ];
                    return safeBenefits.slice(0, 7).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ));
                  })()}
                </ul>
                <p className="text-sm text-green-600 dark:text-green-400 font-semibold border-t border-green-500/20 pt-3">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.benefits.conclusion`) || 'Transforms fuel into soil life.'}
                </p>
              </motion.div>
            </div>

            {/* Key Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-amber-500/10 via-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-6 text-center"
            >
              <p className="text-lg font-semibold text-foreground">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.whyMulching.benefits.statement`) || 'It is the only method that reduces fire risk while regenerating the ecosystem at the same time.'}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Native Forest vs Pine Soil Comparison - Only for De-Pining */}
      {category === 'depining' && (
        <section className="py-12 px-4 bg-gradient-to-b from-background to-amber-950/5">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-600 dark:text-green-400 text-sm font-medium mb-4">
                <Leaf className="w-4 h-4" />
                {language === 'es' ? 'La Ciencia' : 'The Science'}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.title`)}
              </h2>
              <p className="text-lg text-amber-600 dark:text-amber-400 font-medium">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.subtitle`)}
              </p>
              <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.intro`)}
              </p>
            </motion.div>

            {/* Expand/Collapse Toggle */}
            <div className="flex justify-center mb-6">
              <Button
                variant="outline"
                onClick={() => setShowSoilComparison(!showSoilComparison)}
                className="flex items-center gap-2"
                data-testid="soil-comparison-toggle"
              >
                {showSoilComparison ? (
                  <>
                    {language === 'es' ? 'Ocultar detalles científicos' : 'Hide scientific details'}
                    <ChevronDown className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    {language === 'es' ? 'Ver comparación científica completa' : 'View full scientific comparison'}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>

            {/* Summary Cards - Always Visible */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Native Forest */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <TreeDeciduous className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-lg">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.native.title`)}
                    </h3>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.native.species`)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4 p-3 bg-green-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-blue-500" />
                    <span className="text-sm text-muted-foreground">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.native.moistureLabel`)}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-green-600">
                    {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.native.moisture`)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    <span className="text-sm text-muted-foreground">
                      {language === 'es' ? 'Velocidad de propagación' : 'Fire spread speed'}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-green-600">
                    {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.native.spreadSpeed`)}
                  </span>
                </div>
                <p className="mt-4 text-center text-lg font-bold text-green-600 dark:text-green-400">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.summary.native`)}
                </p>
              </motion.div>

              {/* Pine Plantation */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl border border-red-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                    <TreePine className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-lg">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.pine.title`)}
                    </h3>
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.pine.warning`)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4 p-3 bg-red-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-blue-500" />
                    <span className="text-sm text-muted-foreground">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.pine.moistureLabel`)}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-red-600">
                    {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.pine.moisture`)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    <span className="text-sm text-muted-foreground">
                      {language === 'es' ? 'Velocidad de propagación' : 'Fire spread speed'}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-red-600">
                    {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.pine.spreadSpeed`)}
                  </span>
                </div>
                <p className="mt-4 text-center text-lg font-bold text-red-600 dark:text-red-400">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.summary.pine`)}
                </p>
              </motion.div>
            </div>

            {/* 100x Faster Callout */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-6 text-center text-white mb-8"
            >
              <p className="text-3xl md:text-4xl font-black mb-2">100×</p>
              <p className="text-lg font-medium">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.pine.spreadComparison`)}
              </p>
            </motion.div>

            {/* Expandable Detailed Content */}
            <AnimatePresence>
              {showSoilComparison && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  {/* Comparison Table */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card rounded-xl border border-border p-6 mb-8"
                  >
                    <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.comparisonTable.title`)}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            {(() => {
                              const headers = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.comparisonTable.headers`, { returnObjects: true });
                              const safeHeaders = Array.isArray(headers) ? headers : ['Factor', 'Native forest', 'Pine plantation'];
                              return safeHeaders.map((header, idx) => (
                                <th key={idx} className={`py-3 px-4 text-left font-bold ${idx === 0 ? 'text-foreground' : idx === 1 ? 'text-green-600' : 'text-red-600'}`}>
                                  {header}
                                </th>
                              ));
                            })()}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const rows = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.comparisonTable.rows`, { returnObjects: true });
                            const safeRows = Array.isArray(rows) ? rows : [];
                            return safeRows.map((row, idx) => (
                              <tr key={idx} className="border-b border-border/50">
                                <td className="py-3 px-4 text-foreground font-medium">{row[0]}</td>
                                <td className="py-3 px-4 text-green-600 dark:text-green-400">{row[1]}</td>
                                <td className="py-3 px-4 text-red-600 dark:text-red-400">{row[2]}</td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>

                  {/* Fire Intensity */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-xl border border-orange-500/30 p-6 mb-8"
                  >
                    <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <ThermometerSun className="w-5 h-5 text-orange-500" />
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.intensity.title`)}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-green-500/10 rounded-lg p-4 text-center">
                        <p className="text-sm text-muted-foreground mb-1">{language === 'es' ? 'Bosque Nativo' : 'Native Forest'}</p>
                        <p className="text-2xl font-bold text-green-600">
                          {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.intensity.native`)}
                        </p>
                      </div>
                      <div className="bg-red-500/10 rounded-lg p-4 text-center">
                        <p className="text-sm text-muted-foreground mb-1">{language === 'es' ? 'Plantación de Pino' : 'Pine Plantation'}</p>
                        <p className="text-2xl font-bold text-red-600">
                          {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.intensity.pine`)}
                        </p>
                      </div>
                    </div>
                    <p className="text-center text-lg font-bold text-foreground">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.intensity.comparison`)}
                    </p>
                    <p className="text-center text-red-600 dark:text-red-400 font-medium mt-2">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.intensity.warning`)}
                    </p>
                  </motion.div>

                  {/* Fire Ladders */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-card rounded-xl border border-amber-500/30 p-6 mb-8"
                  >
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.fireLadders.title`)}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.fireLadders.intro`)}
                    </p>
                    <div className="grid md:grid-cols-3 gap-4">
                      {['ladder1', 'ladder2', 'ladder3'].map((ladder, idx) => (
                        <div key={ladder} className="bg-amber-500/10 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center text-sm">
                              {idx + 1}
                            </span>
                            <h4 className="font-bold text-foreground text-sm">
                              {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.fireLadders.${ladder}.title`)}
                            </h4>
                          </div>
                          <ul className="space-y-1">
                            {(() => {
                              const points = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.fireLadders.${ladder}.points`, { returnObjects: true });
                              const safePoints = Array.isArray(points) ? points : [];
                              return safePoints.map((point, pIdx) => (
                                <li key={pIdx} className="text-xs text-muted-foreground flex items-start gap-1">
                                  <span className="text-amber-500">•</span>
                                  {point}
                                </li>
                              ));
                            })()}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Water Cycle */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/30 p-6 mb-8"
                  >
                    <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-blue-500" />
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.waterCycle.title`)}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-bold text-green-600 mb-2">
                          {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.waterCycle.native.title`)}
                        </h4>
                        <ul className="space-y-2">
                          {(() => {
                            const points = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.waterCycle.native.points`, { returnObjects: true });
                            const safePoints = Array.isArray(points) ? points : [];
                            return safePoints.map((point, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm text-foreground">
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                {point}
                              </li>
                            ));
                          })()}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-red-600 mb-2">
                          {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.waterCycle.pine.title`)}
                        </h4>
                        <ul className="space-y-2">
                          {(() => {
                            const points = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.waterCycle.pine.points`, { returnObjects: true });
                            const safePoints = Array.isArray(points) ? points : [];
                            return safePoints.map((point, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm text-foreground">
                                <AlertCircle className="w-4 h-4 text-red-500" />
                                {point}
                              </li>
                            ));
                          })()}
                        </ul>
                      </div>
                    </div>
                    <p className="text-center text-muted-foreground mt-4 italic">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.waterCycle.conclusion`)}
                    </p>
                  </motion.div>

                  {/* Feedback Loop */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-red-500/10 rounded-xl border border-red-500/30 p-6 mb-8"
                  >
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.feedback.title`)}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.feedback.intro`)}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(() => {
                        const points = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.feedback.points`, { returnObjects: true });
                        const safePoints = Array.isArray(points) ? points : [];
                        return safePoints.map((point, idx) => (
                          <span key={idx} className="px-3 py-1 bg-red-500/20 text-red-600 dark:text-red-400 rounded-full text-sm font-medium">
                            {point}
                          </span>
                        ));
                      })()}
                    </div>
                    <p className="text-red-600 dark:text-red-400 font-bold">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.feedback.conclusion`)}
                    </p>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Public Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border-2 border-amber-500 p-6 text-center mb-8"
            >
              <p className="text-xl md:text-2xl font-bold text-foreground italic">
                &ldquo;{t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.publicQuote`)}&rdquo;
              </p>
            </motion.div>

            {/* Why De-Pining Works */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30 p-8"
            >
              <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Sprout className="w-6 h-6 text-green-600" />
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.whyWorks.title`)}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.whyWorks.intro`)}
              </p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {(() => {
                  const points = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.whyWorks.points`, { returnObjects: true });
                  const safePoints = Array.isArray(points) ? points : [];
                  return safePoints.map((point, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-green-500/10 rounded-lg p-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-foreground">{point}</span>
                    </div>
                  ));
                })()}
              </div>
              <p className="text-lg font-bold text-green-600 dark:text-green-400 text-center">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.soilComparison.whyWorks.conclusion`)}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Two Complementary Methods - Only for De-Pining */}
      {data.twoMethods && (
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            {/* Intro */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-amber-500/30 p-8 mb-8"
            >
              <p className="text-foreground leading-relaxed mb-4">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.intro`) || data.twoMethods.intro}
              </p>
              <p className="text-amber-600 dark:text-amber-400 font-medium">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.approach`) || data.twoMethods.approach}
              </p>
            </motion.div>

            {/* Two Methods Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Mechanical Method */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-amber-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <span className="text-2xl">🔧</span>
                  </div>
                  <div>
                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">1️⃣</span>
                    <h3 className="font-bold text-foreground">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.mechanical.title`) || data.twoMethods.mechanical.title}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.mechanical.description`) || data.twoMethods.mechanical.description}
                </p>
                <ul className="space-y-2 mb-4">
                  {(() => {
                    const items = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.mechanical.items`);
                    const safeItems = Array.isArray(items) ? items : data.twoMethods.mechanical.items;
                    return safeItems.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="w-4 h-4 text-amber-500" />
                        {item}
                      </li>
                    ));
                  })()}
                </ul>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium italic">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.mechanical.note`) || data.twoMethods.mechanical.note}
                </p>
              </motion.div>

              {/* Manual Method */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-2xl">🌲</span>
                  </div>
                  <div>
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">2️⃣</span>
                    <h3 className="font-bold text-foreground">
                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.manual.title`) || data.twoMethods.manual.title}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.manual.description`) || data.twoMethods.manual.description}
                </p>
                <ul className="space-y-1 mb-4">
                  {(() => {
                    const zones = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.manual.zones`);
                    const safeZones = Array.isArray(zones) ? zones : data.twoMethods.manual.zones;
                    return safeZones.map((zone, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        {zone}
                      </li>
                    ));
                  })()}
                </ul>
                <p className="text-sm text-muted-foreground mb-2">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.manual.method`) || data.twoMethods.manual.method}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 font-medium italic">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.twoMethods.manual.note`) || data.twoMethods.manual.note}
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Local Employment Program - Only for De-Pining */}
      {data.employmentProgram && (
        <section className="py-12 px-4 bg-gradient-to-b from-green-500/5 to-background">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 rounded-full text-green-600 dark:text-green-400 text-sm font-medium mb-4">
                <span className="text-lg">👷</span>
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.employment.title`) || data.employmentProgram.title}
              </div>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.employment.intro`) || data.employmentProgram.intro}
              </p>
            </motion.div>

            {/* Employment Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl p-4 border border-green-500/30 text-center"
              >
                <p className="text-4xl font-bold text-green-600 dark:text-green-400">{data.employmentProgram.workers}</p>
                <p className="text-sm text-muted-foreground">{t('common.fullTimeWorkers') || 'Full-time Workers'}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-card rounded-xl p-4 border border-green-500/30 text-center"
              >
                <p className="text-4xl font-bold text-green-600 dark:text-green-400">${data.employmentProgram.salary.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">{t('common.monthlyPerWorker') || 'Monthly per Worker'}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-card rounded-xl p-4 border border-green-500/30 text-center"
              >
                <p className="text-4xl font-bold text-green-600 dark:text-green-400">{data.employmentProgram.duration}</p>
                <p className="text-sm text-muted-foreground">{t('common.months') || 'Months (5 Years)'}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-card rounded-xl p-4 border border-green-500/30 text-center"
              >
                <p className="text-4xl font-bold text-green-600 dark:text-green-400">${(data.employmentProgram.totalSalaries / 1000000).toFixed(1)}M</p>
                <p className="text-sm text-muted-foreground">{t('common.totalSalaries') || 'Total Salaries'}</p>
              </motion.div>
            </div>

            {/* Priority Hiring */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-green-500/30 p-6 mb-8 text-center"
            >
              <p className="text-green-600 dark:text-green-400 font-medium">
                ⭐ {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.employment.priority`) || data.employmentProgram.priority}
              </p>
            </motion.div>

            {/* Manual Equipment */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-lg">🪚</span>
                  {t('common.chainsaws') || 'Chainsaws'}
                </h4>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">{t('common.quantity') || 'Quantity'}</span>
                  <span className="font-bold text-foreground">{data.employmentProgram.equipment.chainsaws.quantity} {t('common.units') || 'units'}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">{t('common.costPerUnit') || 'Cost per unit'}</span>
                  <span className="font-bold text-foreground">${data.employmentProgram.equipment.chainsaws.costPerUnit.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <span className="text-muted-foreground font-medium">{t('common.total') || 'Total'}</span>
                  <span className="font-bold text-green-600 dark:text-green-400">${data.employmentProgram.equipment.chainsaws.total.toLocaleString()}</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-lg">🦺</span>
                  {t('common.safetyEquipment') || 'Safety & Tools'}
                </h4>
                <ul className="space-y-2 mb-4">
                  {(() => {
                    const items = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.employment.equipmentItems`);
                    const safeItems = Array.isArray(items) ? items : data.employmentProgram.equipment.items;
                    return safeItems.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        {item}
                      </li>
                    ));
                  })()}
                </ul>
                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <span className="text-muted-foreground font-medium">{t('common.total') || 'Total'}</span>
                  <span className="font-bold text-green-600 dark:text-green-400">${data.employmentProgram.equipment.safetyGear.total.toLocaleString()}</span>
                </div>
              </motion.div>
            </div>

            {/* Manual De-Pining Cost Summary */}
            {data.manualCostBreakdown && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-8"
              >
                <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  {t('common.manualDepiningCost') || 'Manual De-Pining Cost Summary (5 years)'}
                </h4>
                <div className="space-y-3">
                  {(() => {
                    const costCategories = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.employment.manualCostCategories`);
                    const hasCostTranslations = Array.isArray(costCategories);
                    return data.manualCostBreakdown.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-green-500/20 last:border-0">
                        <span className="text-foreground">{hasCostTranslations ? costCategories[idx] : (language === 'es' && item.categoryEs ? item.categoryEs : item.category)}</span>
                        <span className="font-bold text-green-600 dark:text-green-400">
                          ${(item.cost / 1000000).toFixed(item.cost >= 1000000 ? 1 : 3)}M
                        </span>
                      </div>
                    ));
                  })()}
                  <div className="flex justify-between items-center pt-4 border-t-2 border-green-500">
                    <span className="text-lg font-bold text-foreground">{t('common.totalManualProgram') || 'Total Manual De-Pining Program'}</span>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                      ${(data.manualCostBreakdown.reduce((sum, item) => sum + item.cost, 0) / 1000000).toFixed(2)}M
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Social & Environmental Impact - Only for De-Pining */}
      {data.socialImpact && (
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Social Impact */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-amber-500/30 p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-xl">🌱</span>
                  {t('common.socialEnvironmentalImpact')}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{t('common.thisPlanWill')}</p>
                <div className="space-y-2">
                  {(() => {
                    const socialImpactItems = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.results.items`, { returnObjects: true });
                    const safeItems = Array.isArray(socialImpactItems) ? socialImpactItems : data.socialImpact;
                    return safeItems.map((impact, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-foreground text-sm">{impact}</span>
                      </div>
                    ));
                  })()}
                </div>
              </motion.div>

              {/* Transparency */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/30 p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-xl">🧭</span>
                  {t('common.transparency')}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{t('common.allWorkWillBe')}</p>
                <div className="space-y-2">
                  {(() => {
                    const transparencyItems = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.transparency.items`, { returnObjects: true });
                    const safeItems = Array.isArray(transparencyItems) ? transparencyItems : data.transparency;
                    return safeItems.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-foreground text-sm">{item}</span>
                      </div>
                    ));
                  })()}
                </div>
                <p className="mt-4 text-sm text-blue-600 dark:text-blue-400 font-medium italic">
                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.transparency.conclusion`)}
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Geography Challenges & Boat Capabilities - Only for Firefighting */}
      {data.geographyChallenges && (
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Geography Challenges */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-red-500/30 p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Flame className="w-5 h-5 text-red-500" />
                  {t(`landing.donationCategories.${data.translationKey}.boatSystem.challengesTitle`) || "Why Water-Based Response?"}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t(`landing.donationCategories.${data.translationKey}.boatSystem.challengesIntro`) || "Epuyén's geography creates three major challenges:"}
                </p>
                <div className="space-y-2">
                  {(() => {
                  const challenges = t(`landing.donationCategories.${data.translationKey}.boatSystem.challenges`);
                  const items = Array.isArray(challenges) ? challenges : data.geographyChallenges;
                  return items.map((challenge, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center text-red-500 font-bold text-xs">{idx + 1}</span>
                      <span className="text-foreground">{challenge}</span>
                    </div>
                  ));
                })()}
                </div>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium mt-4">
                  {t(`landing.donationCategories.${data.translationKey}.boatSystem.solutionStatement`) || "Water-based response solves all three."}
                </p>
              </motion.div>

              {/* Boat Capabilities */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl border border-red-500/30 p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-red-500" />
                  {t(`landing.donationCategories.${data.translationKey}.boatSystem.capabilitiesTitle`) || "A boat can:"}
                </h3>
                <div className="space-y-3">
                  {(() => {
                  const capabilities = t(`landing.donationCategories.${data.translationKey}.boatSystem.capabilities`);
                  const items = Array.isArray(capabilities) ? capabilities : data.boatCapabilities;
                  return items.map((capability, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground text-sm">{capability}</span>
                    </div>
                  ));
                })()}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Research Phase Section - Only for Contamination */}
      {data.isResearchPhase && data.researchPhase && (
        <section className="py-16 px-4 bg-cyan-500/5 border-y border-cyan-500/20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 rounded-full text-cyan-600 dark:text-cyan-400 text-sm font-medium mb-4">
                <AlertCircle className="w-4 h-4" />
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.currentPhase`) || data.researchPhase.currentPhase}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-cyan-500/30 p-8 mb-8"
            >
              <p className="text-foreground leading-relaxed mb-4">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.context`) || data.researchPhase.context}
              </p>
              <p className="text-cyan-600 dark:text-cyan-400 font-medium">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.statement`) || data.researchPhase.statement}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-border p-8 mb-8"
            >
              <h3 className="text-lg font-bold text-foreground mb-4">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.focusTitle`) || 'Current Focus Areas:'}
              </h3>
              <div className="space-y-3">
              {(() => {
                const focusAreas = t(`landing.donationCategories.${data.translationKey}.researchPhase.focusAreas`);
                const items = Array.isArray(focusAreas) ? focusAreas : data.researchPhase.focusAreas;
                return items.map((area, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-cyan-600 dark:text-cyan-400 text-xs font-bold">{idx + 1}</span>
                    </div>
                    <span className="text-foreground">{area}</span>
                  </div>
                ));
              })()}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-cyan-500/10 to-teal-500/10 rounded-xl border border-cyan-500/30 p-8 mb-8"
            >
              <p className="text-muted-foreground mb-4">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.requirement`) || data.researchPhase.requirement}
              </p>
              <p className="text-cyan-600 dark:text-cyan-400 font-semibold text-lg mb-4">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.ethos`) || data.researchPhase.ethos}
              </p>
              <p className="text-foreground">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.commitment`) || data.researchPhase.commitment}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-border p-8 text-center"
            >
              <p className="text-foreground font-medium mb-4">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.objective`) || data.researchPhase.objective}
              </p>
              <p className="text-muted-foreground italic">
                {t(`landing.donationCategories.${data.translationKey}.researchPhase.importance`) || data.researchPhase.importance}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Phases Section */}
      <section className="py-16 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
            <Target className="w-6 h-6" />
            {t('donate.phasesAndMilestones')}
          </h2>
          
          <div className="space-y-6">
            {data.phases.map((phase, idx) => {
              const phaseProgress = (phase.raised / phase.goal) * 100;
              const yearKey = `year${phase.id}`;
              const translatedTitle = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.phases.${yearKey}.title`);
              const translatedItems = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.phases.${yearKey}.items`, { returnObjects: true });
              const phaseTitle = translatedTitle && !translatedTitle.includes('fiveYearPlan') ? translatedTitle : (language === 'es' ? phase.titleEs : phase.title);
              const phaseMilestones = Array.isArray(translatedItems) ? translatedItems : phase.milestones;
              
              return (
                <motion.div
                  key={phase.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={`bg-card rounded-xl p-6 border ${phase.status === 'active' ? data.borderColor : 'border-border'}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${data.bgColor}`}>
                        {phase.id}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-foreground">{phaseTitle}</h3>
                          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs">
                            {getStatusIcon(phase.status)}
                            <span>{getStatusLabel(phase.status)}</span>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm">{language === 'es' ? phase.descriptionEs : phase.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold ${data.accentColor}`}>${phase.raised.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{t('donate.ofGoal')} ${phase.goal.toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {/* Phase Progress */}
                  <div className="mb-4">
                    <Progress value={phaseProgress} className="h-2" />
                  </div>
                  
                  {/* Milestones */}
                  <div className="flex flex-wrap gap-2">
                    {phaseMilestones.map((milestone, mIdx) => (
                      <span 
                        key={mIdx}
                        className={`text-xs px-3 py-1 rounded-full ${
                          mIdx === 0 && phase.status === 'active' 
                            ? `${data.bgColor} text-white` 
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {milestone}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Equipment List */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <DollarSign className="w-6 h-6" />
              {t('donate.equipmentAndInfrastructure')}
            </h2>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">{t('donate.totalEquipmentCost')}</p>
              <p className={`text-2xl font-bold ${data.accentColor}`}>${totalEquipmentCost.toLocaleString()}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {data.equipment.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className={`bg-card rounded-xl border ${item.priority === 'high' ? data.borderColor : 'border-border'} p-6`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-foreground">{item.name}</h3>
                      {item.priority === 'high' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${data.bgColor} text-white`}>
                          {t('donate.priority')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{item.brand}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('donate.phase')} {item.phase}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${data.accentColor}`}>${item.cost.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">× {item.quantity} = ${(item.cost * item.quantity).toLocaleString()}</p>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                
                {item.whyRequired && (
                  <p className={`text-sm ${category === 'firefighting' ? 'text-red-700 dark:text-red-300 bg-red-500/10' : 'text-amber-700 dark:text-amber-300 bg-amber-500/10'} rounded-lg p-2 mb-3 italic`}>
                    💡 {item.whyRequired}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-2">
                  {item.specs.map((spec, specIdx) => (
                    <span key={specIdx} className="text-xs px-2 py-1 bg-muted rounded-full text-muted-foreground">
                      {spec}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* CAPEX Breakdown - For De-Pining and Firefighting */}
          {data.capexBreakdown && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`mt-12 bg-card rounded-xl border ${category === 'firefighting' ? 'border-red-500/30' : 'border-amber-500/30'} p-8`}
            >
              <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                <DollarSign className={`w-5 h-5 ${category === 'firefighting' ? 'text-red-600' : 'text-amber-600'}`} />
                {category === 'firefighting' 
                  ? t(`landing.donationCategories.${data.translationKey}.boatSystem.capex.title`)
                  : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.title`)
                }
              </h3>
              <div className="space-y-3">
                {data.capexBreakdown.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-foreground">{language === 'es' && item.categoryEs ? item.categoryEs : item.category}</span>
                    <span className={`font-bold ${category === 'firefighting' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      ${(item.cost / 1000000).toFixed(2)}M
                    </span>
                  </div>
                ))}
                <div className={`flex justify-between items-center pt-4 border-t-2 ${category === 'firefighting' ? 'border-red-500' : 'border-amber-500'}`}>
                  <span className="text-lg font-bold text-foreground">
                    {category === 'firefighting'
                      ? t(`landing.donationCategories.${data.translationKey}.boatSystem.capex.total`)
                      : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.total`)
                    }
                  </span>
                  <span className={`text-2xl font-bold ${category === 'firefighting' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    ${(data.capexBreakdown.reduce((sum, item) => sum + item.cost, 0) / 1000000).toFixed(1)}M
                  </span>
                </div>

                {/* Argentina Landed Cost Section - Only for De-Pining */}
                {category === 'depining' && (
                  <div className="mt-6 pt-6 border-t border-border">
                    {/* Callout Box */}
                    <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-5">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setShowArgentinaDetails(!showArgentinaDetails)}
                        data-testid="argentina-cost-toggle"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h4 className="font-bold text-foreground">
                              {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.title`)}
                            </h4>
                            <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                              {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.totalRange`)}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                          {showArgentinaDetails 
                            ? t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.learnLess`)
                            : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.learnMore`)
                          }
                          {showArgentinaDetails ? <ChevronDown className="w-4 h-4 ml-1" /> : <ChevronRight className="w-4 h-4 ml-1" />}
                        </Button>
                      </div>

                      <AnimatePresence>
                        {showArgentinaDetails && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-5 pt-5 border-t border-blue-500/20">
                              {/* Intro */}
                              <p className="text-sm text-muted-foreground mb-4">
                                {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.intro`)}
                              </p>

                              {/* Key Points */}
                              <div className="space-y-3 mb-5">
                                {(() => {
                                  const points = t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.points`, { returnObjects: true });
                                  const safePoints = Array.isArray(points) ? points : [];
                                  const icons = [Ship, Info, Globe, Wrench];
                                  return safePoints.map((point, idx) => {
                                    const Icon = icons[idx % icons.length];
                                    return (
                                      <div key={idx} className="flex items-start gap-3">
                                        <Icon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-foreground">{point}</span>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>

                              {/* Cost Breakdown Table */}
                              <div className="bg-card/50 rounded-lg p-4 mb-4">
                                <h5 className="font-bold text-foreground mb-3 flex items-center gap-2">
                                  <DollarSign className="w-4 h-4 text-blue-600" />
                                  {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.title`)}
                                </h5>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center py-2 border-b border-border">
                                    <span className="text-sm text-muted-foreground">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.baseMachinery`)}
                                    </span>
                                    <span className="text-sm font-medium text-foreground">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.baseRange`)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 border-b border-border">
                                    <span className="text-sm text-muted-foreground">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.importTaxes`)}
                                    </span>
                                    <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.importRange`)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 border-b border-border">
                                    <span className="text-sm text-muted-foreground">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.spareParts`)}
                                    </span>
                                    <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.spareRange`)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center pt-3 border-t-2 border-blue-500">
                                    <span className="font-bold text-foreground">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.totalLanded`)}
                                    </span>
                                    <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                      {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.costBreakdown.totalRange`)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Disclaimer - Always visible */}
                      <div className="mt-4 pt-4 border-t border-blue-500/20">
                        <p className="text-xs text-blue-600 dark:text-blue-400 italic flex items-start gap-2">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          {t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.capex.argentinaLanded.disclaimer`)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Strategic Importance - For De-Pining and Firefighting */}
          {data.strategicImportance && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`mt-8 bg-gradient-to-r ${category === 'firefighting' ? 'from-red-500/10 to-orange-500/10 border-red-500/30' : 'from-amber-500/10 to-orange-500/10 border-amber-500/30'} rounded-xl border p-8`}
            >
              <h3 className="text-xl font-bold text-foreground mb-4">
                {category === 'firefighting'
                  ? t(`landing.donationCategories.${data.translationKey}.boatSystem.strategic.title`)
                  : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.strategic.title`)
                }
              </h3>
              <p className="text-muted-foreground mb-4">
                {category === 'firefighting'
                  ? t(`landing.donationCategories.${data.translationKey}.boatSystem.strategic.establishes`)
                  : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.strategic.establishes`)
                }
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {(() => {
                  const items = category === 'firefighting'
                    ? t(`landing.donationCategories.${data.translationKey}.boatSystem.strategic.items`)
                    : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.strategic.items`);
                  const safeItems = Array.isArray(items) ? items : data.strategicImportance;
                  return safeItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-card rounded-lg">
                      <CheckCircle2 className={`w-5 h-5 ${category === 'firefighting' ? 'text-red-500' : 'text-amber-500'} flex-shrink-0`} />
                      <span className="text-foreground">{item}</span>
                    </div>
                  ));
                })()}
              </div>
              <p className="mt-6 text-sm text-muted-foreground italic">
                {category === 'firefighting'
                  ? t(`landing.donationCategories.${data.translationKey}.boatSystem.strategic.note`)
                  : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.strategic.note`)
                }
              </p>
            </motion.div>
          )}

          {/* Conclusion - For De-Pining and Firefighting */}
          {data.conclusion && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-card rounded-xl border border-border p-8 text-center"
            >
              <p className="text-lg text-foreground max-w-4xl mx-auto leading-relaxed">
                {category === 'firefighting' 
                  ? t(`landing.donationCategories.${data.translationKey}.boatSystem.conclusion`)
                  : t(`landing.donationCategories.${data.translationKey}.fiveYearPlan.conclusion`)
                }
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Cost Explanation Section - Only for De-Pining */}
      {category === 'depining' && (
        <section className="py-16 px-4 bg-gradient-to-b from-amber-500/5 to-background">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full text-amber-600 dark:text-amber-400 text-sm font-medium mb-4">
                <DollarSign className="w-4 h-4" />
                {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.title') || 'Cost Explanation'}
              </div>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.intro') || 'Real wildfire prevention cannot be achieved through isolated actions or short-term interventions.'}
              </p>
              <p className="text-foreground font-medium mt-4 max-w-3xl mx-auto">
                {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.requirement') || 'To protect Epuyén, a continuous five-year plan is required.'}
              </p>
            </motion.div>

            {/* Two Methods Comparison */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Mechanical Method */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-amber-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <span className="text-2xl">🔧</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.title') || '1. Mechanical De-Pining'}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.description') || 'This method is used in areas where invasive pine dominates the landscape.'}
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium mb-4">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.capability') || 'It allows large-scale intervention and rapid reduction of dangerous fuel loads.'}
                </p>
                
                <h4 className="font-semibold text-foreground mb-2 text-sm">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.includes') || 'What this cost includes'}
                </h4>
                <ul className="space-y-1 mb-4">
                  {(() => {
                    const items = t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.items');
                    if (Array.isArray(items)) {
                      return items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          {item}
                        </li>
                      ));
                    }
                    return null;
                  })()}
                </ul>

                <div className="bg-amber-500/10 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.oneTimeInvestment') || 'One-time investment:'}
                    </span>
                    <span className="font-bold text-amber-600 dark:text-amber-400">
                      {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.oneTimeRange') || 'USD 3.0 – 5.8 million'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.operationalCost') || 'Operational cost (5 years):'}
                    </span>
                    <span className="font-bold text-amber-600 dark:text-amber-400">
                      {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.operationalRange') || 'USD 6.1 – 10.7 million'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic mt-3">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.mechanical.note') || 'This investment creates permanent wildfire-prevention capacity.'}
                </p>
              </motion.div>

              {/* Manual Method */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-2xl">👷</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.title') || '2. Manual / Surgical De-Pining'}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.description') || 'In many areas, invasive pine grows mixed with native forest.'}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.limitation') || 'Heavy machinery cannot be used without destroying native ecosystems.'}
                </p>

                <h4 className="font-semibold text-foreground mb-2 text-sm">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.whereApplied') || 'Where it is applied'}
                </h4>
                <ul className="space-y-1 mb-4">
                  {(() => {
                    const zones = t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.zones');
                    if (Array.isArray(zones)) {
                      return zones.map((zone, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          {zone}
                        </li>
                      ));
                    }
                    return null;
                  })()}
                </ul>

                <div className="bg-green-500/10 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.workers') || '50 full-time workers'}</span>
                    <span className="text-foreground font-medium">×</span>
                    <span className="text-muted-foreground">{t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.salary') || 'USD 1,000/month'}</span>
                    <span className="text-foreground font-medium">×</span>
                    <span className="text-muted-foreground">{t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.duration') || '5 years'}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-green-500/30">
                    <span className="text-sm font-medium text-foreground">
                      {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.totalCost') || 'Total manual de-pining (5 years):'}
                    </span>
                    <span className="font-bold text-green-600 dark:text-green-400 text-lg">
                      {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.manual.totalAmount') || 'USD 3.125 million'}
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Total Cost Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-green-500/10 rounded-2xl border border-amber-500/30 p-8 mb-12"
            >
              <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
                {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.totalCost.title') || 'Total Cost — Five-Year Prevention Plan'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-amber-500/30">
                      <th className="text-left py-3 px-4 text-foreground font-semibold">
                        {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.totalCost.componentHeader') || 'Component'}
                      </th>
                      <th className="text-right py-3 px-4 text-foreground font-semibold">
                        {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.totalCost.costHeader') || 'Cost'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const components = t('landing.donationCategories.category1.fiveYearPlan.costExplanation.totalCost.components');
                      if (Array.isArray(components)) {
                        return components.map((item, idx) => (
                          <tr key={idx} className="border-b border-amber-500/20">
                            <td className="py-3 px-4 text-muted-foreground">{item.category}</td>
                            <td className="py-3 px-4 text-right font-medium text-foreground">{item.cost}</td>
                          </tr>
                        ));
                      }
                      return null;
                    })()}
                  </tbody>
                  <tfoot>
                    <tr className="bg-amber-500/10">
                      <td className="py-4 px-4 font-bold text-foreground text-lg">
                        {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.totalCost.total') || 'Total prevention program'}
                      </td>
                      <td className="py-4 px-4 text-right font-bold text-amber-600 dark:text-amber-400 text-xl">
                        {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.totalCost.totalRange') || 'USD 12.2 – 19.6 million'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </motion.div>

            {/* What This Plan Delivers + Why Five Years */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* What This Plan Delivers */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-xl">🌱</span>
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.delivers.title') || 'What this plan delivers'}
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.delivers.intro') || 'Over five years, this program enables:'}
                </p>
                <ul className="space-y-2">
                  {(() => {
                    const items = t('landing.donationCategories.category1.fiveYearPlan.costExplanation.delivers.items');
                    if (Array.isArray(items)) {
                      return items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ));
                    }
                    return null;
                  })()}
                </ul>
                <div className="mt-4 p-3 bg-amber-500/10 rounded-lg">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.delivers.notEmergency') || 'This is not emergency response.'}{' '}
                    <span className="text-foreground">{t('landing.donationCategories.category1.fiveYearPlan.costExplanation.delivers.structural') || 'It is structural prevention.'}</span>
                  </p>
                </div>
              </motion.div>

              {/* Why Five Years */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-xl">🔥</span>
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.whyFiveYears.title') || 'Why five years?'}
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.whyFiveYears.intro') || 'Ecosystems do not stabilize in months.'}
                </p>
                <p className="text-sm text-foreground mb-3">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.whyFiveYears.allows') || 'A five-year period allows:'}
                </p>
                <ul className="space-y-2">
                  {(() => {
                    const items = t('landing.donationCategories.category1.fiveYearPlan.costExplanation.whyFiveYears.items');
                    if (Array.isArray(items)) {
                      return items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-foreground">
                          <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          {item}
                        </li>
                      ));
                    }
                    return null;
                  })()}
                </ul>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium mt-4">
                  {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.whyFiveYears.conclusion') || 'Shorter timelines do not break the fire cycle.'}
                </p>
              </motion.div>
            </div>

            {/* Transparency Note */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-primary/30 p-8 text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
                <AlertCircle className="w-4 h-4" />
                {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.transparency.title') || 'Transparency'}
              </div>
              <p className="text-muted-foreground mb-4">
                {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.transparency.intro') || 'All funds will be:'}
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {(() => {
                  const items = t('landing.donationCategories.category1.fiveYearPlan.costExplanation.transparency.items');
                  if (Array.isArray(items)) {
                    return items.map((item, idx) => (
                      <span key={idx} className="px-3 py-1 bg-primary/10 rounded-full text-sm text-foreground">
                        {item}
                      </span>
                    ));
                  }
                  return null;
                })()}
              </div>
              <p className="text-lg font-medium text-primary">
                {t('landing.donationCategories.category1.fiveYearPlan.costExplanation.transparency.conclusion') || "Prevention is not a cost — it is the only way to protect Epuyén's future."}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`bg-gradient-to-r ${data.bgGradient} rounded-2xl p-8 text-center border ${data.borderColor}`}
          >
            <h3 className="text-2xl font-bold text-foreground mb-4">
              {t('landing.donationCategories.cta.supportMission') || 'Support This Mission'}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              {t('landing.donationCategories.cta.ctaDescription') || 'Every donation goes directly to acquiring equipment and completing these phases. All expenses are publicly documented with photos, receipts, and GPS data.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                style={{ backgroundColor: data.color === 'amber' ? '#d97706' : data.color === 'red' ? '#dc2626' : data.color === 'purple' ? '#9333ea' : '#16a34a' }}
              >
                <DollarSign className="w-5 h-5 mr-2" />
                {t('landing.donationCategories.cta.donateToCategory') || 'Donate to This Category'}
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/transparency">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('landing.donationCategories.cta.viewTransparency') || 'View Transparency Records'}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Economic Impact Section - Only for Biosphere */}
      {data.economicImpact && (
        <section className="py-16 px-4 bg-gradient-to-b from-purple-500/5 to-background">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 rounded-full text-purple-600 dark:text-purple-400 text-sm font-medium mb-4">
                <Target className="w-4 h-4" />
                Economic Impact Summary
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                33 Hectare Biosphere & Community Development
              </h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                A long-term ecological and community reconstruction initiative transforming post-fire recovery into a permanent regional development model.
              </p>
            </motion.div>

            {/* Location Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-purple-500/30 p-6 mb-8"
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h3 className="font-bold text-foreground text-lg">📍 {data.economicImpact.location}</h3>
                  <p className="text-muted-foreground">{data.economicImpact.area}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {data.economicImpact.regionalIntegration.map((region, idx) => (
                    <span key={idx} className="px-3 py-1 bg-purple-500/10 rounded-full text-sm text-purple-600 dark:text-purple-400">
                      {region}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Employment Impact Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Direct Employment */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-2xl">👷</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">Direct Employment</h3>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {data.economicImpact.directJobs.min}–{data.economicImpact.directJobs.max}
                    </p>
                    <p className="text-sm text-muted-foreground">{data.economicImpact.directJobs.type}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">Positions include:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {data.economicImpact.directJobs.positions.map((position, idx) => (
                      <span key={idx} className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        {position}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm font-medium text-foreground mb-2">Priority Hiring:</p>
                    {data.economicImpact.directJobs.priority.map((p, idx) => (
                      <span key={idx} className="inline-block text-xs px-2 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-full mr-2 mb-2">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Indirect Employment */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-2xl">🤝</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">Indirect Employment</h3>
                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                      {data.economicImpact.indirectJobs.estimate}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">Sectors benefited:</p>
                  <div className="space-y-2">
                    {data.economicImpact.indirectJobs.sectors.map((sector, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ChevronRight className="w-4 h-4 text-blue-500" />
                        {sector}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Economic & Environmental Benefits */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {/* Economic Circulation */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <div className="text-3xl mb-3">💰</div>
                <h4 className="font-bold text-foreground mb-3">Economic Circulation</h4>
                <ul className="space-y-2">
                  {data.economicImpact.economicCirculation.map((item, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Community Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <div className="text-3xl mb-3">🏘️</div>
                <h4 className="font-bold text-foreground mb-3">Community Benefits</h4>
                <ul className="space-y-2">
                  {data.economicImpact.benefits.map((item, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Tourism Profile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-card rounded-xl border border-border p-6"
              >
                <div className="text-3xl mb-3">🎯</div>
                <h4 className="font-bold text-foreground mb-3">Tourism & Attraction</h4>
                <ul className="space-y-2">
                  {data.economicImpact.tourismProfile.map((item, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Environmental Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30 p-8 mb-8"
            >
              <h4 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Sprout className="w-6 h-6 text-green-600" />
                Environmental Services Value
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground mb-3">Long-term public value:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {data.economicImpact.environmentalServices.map((service, idx) => (
                      <span key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-3">Reduces future public expenditure on:</p>
                  <div className="flex flex-wrap gap-2">
                    {data.economicImpact.publicSavings.map((saving, idx) => (
                      <span key={idx} className="px-3 py-1 bg-red-500/10 text-red-600 dark:text-red-400 rounded-full text-sm">
                        {saving}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Strategic Value for Municipality */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl border border-purple-500/30 p-8"
            >
              <h4 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Target className="w-6 h-6 text-purple-600" />
                Strategic Value for the Municipality of Epuyén
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                {data.economicImpact.municipalValue.map((value, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-purple-500/5 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    <span className="text-sm text-foreground">{value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-border text-center">
                <p className="text-muted-foreground italic max-w-3xl mx-auto">
                  &ldquo;Epuyén Renace proposes not only environmental recovery, but a structural response to ecological disaster. 
                  By integrating restoration, prevention, education, and sustainable economic activity, the project contributes 
                  to long-term social stability and economic resilience for Epuyén and the wider region.&rdquo;
                </p>
              </div>
            </motion.div>

            {/* Link to Full Biosphere Park Page */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <Link to="/biosphere">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white px-8" data-testid="view-biosphere-plan-btn">
                  <TreePine className="w-5 h-5 mr-2" />
                  {language === 'es' ? 'Ver Plan Completo del Parque Biosfera' : 'View Full Biosphere Park Plan'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-card border-t border-border py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-muted-foreground text-sm">
          <p>{t('footer.tagline')}</p>
          <p className="mt-2">{t('footer.copyright').replace('{year}', new Date().getFullYear())}</p>
        </div>
      </footer>
    </div>
  );
};

export default DonationCategoryPage;
